/**
 * /admin/platform/audit — cross-tenant audit feed.
 */

import { useEffect, useState } from "react";
import { adminApi, type AdminApiError } from "./api";

interface AuditRow {
  event_id: string;
  tenant_id: string | null;
  kind: string;
  occurred_at: string;
  user_id: string | null;
  service_account_id: string | null;
  request_id: string | null;
  payload: unknown;
}

export function AuditPage() {
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const result = await adminApi.crossTenantAudit();
        setRows(result.events);
      } catch (exc) {
        const apiErr = exc as AdminApiError;
        setError(apiErr.message ?? "Could not load audit.");
      }
    })();
  }, []);

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <h1>Audit</h1>
        <p className="admin-page-sub">
          Cross-tenant audit feed. Events are written for every
          authentication, admin action, and assessment step on every
          tenant; only platform owners see this combined view.
        </p>
      </header>

      {error !== null && <div className="auth-error" role="alert">{error}</div>}

      <table className="admin-table admin-table-audit">
        <thead>
          <tr>
            <th>When</th>
            <th>Tenant</th>
            <th>Kind</th>
            <th>Actor</th>
            <th>Payload</th>
          </tr>
        </thead>
        <tbody>
          {rows === null ? (
            <tr>
              <td colSpan={5} className="admin-empty">Loading…</td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="admin-empty">
                No audit events recorded yet. Events accumulate as users
                act on the platform.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.event_id}>
                <td>{new Date(r.occurred_at).toLocaleString()}</td>
                <td>{r.tenant_id ?? "platform"}</td>
                <td><code>{r.kind}</code></td>
                <td>
                  {r.user_id ?? r.service_account_id ?? "—"}
                </td>
                <td>
                  <pre className="admin-payload">
                    {r.payload === null
                      ? "—"
                      : JSON.stringify(r.payload, null, 0).slice(0, 200)}
                  </pre>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
